import React from 'react';
import FeaturePageTemplate from '../components/FeaturePageTemplate';

const ProfilingPage = () => {
  const icon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );

  const benefits = [
    "Gain instant insights into data quality and structure without writing code",
    "Quickly identify potential quality issues or anomalies in your data",
    "Understand data distributions, patterns, and outliers at a glance",
    "Establish baseline metrics for ongoing monitoring",
    "Share comprehensive data profiles with stakeholders"
  ];

  const details = [
    {
      title: "Column-Level Statistics",
      description: "Qupid automatically generates comprehensive statistical profiles for each column in your tables, providing immediate insight into your data's shape and quality.",
      items: [
        "Distribution analysis for numeric, categorical, and date fields",
        "Outlier detection and visualization",
        "NULL value and empty string analysis",
        "Cardinality and uniqueness assessment",
        "Min, max, mean, median, and standard deviation calculations"
      ]
    },
    {
      title: "Table Structure Analysis",
      description: "Get a comprehensive view of table structures and relationships to understand your data's organization.",
      items: [
        "Row count and volume trending over time",
        "Relationship detection between tables",
        "Primary key and foreign key validation",
        "Indexing analysis and recommendations",
        "Table growth and change rate monitoring"
      ]
    },
    {
      title: "Data Type Inference and Validation",
      description: "Validate that data content matches expected data types and identify potential misalignments.",
      items: [
        "Automatic data type inference and validation",
        "Detection of mixed data types within columns",
        "Format pattern recognition for strings",
        "Data type conversion recommendations",
        "Constraint validation"
      ]
    }
  ];

  const useCases = [
    {
      title: "New Data Source Onboarding",
      description: "Quickly understand the structure and quality of newly connected data sources before integrating them into critical business processes."
    },
    {
      title: "Data Migration Validation",
      description: "Compare data profiles before and after migration to ensure data integrity has been maintained throughout the process."
    },
    {
      title: "Data Quality Baseline Establishment",
      description: "Create baseline metrics for ongoing data quality monitoring, allowing you to detect deviations from expected norms over time."
    },
    {
      title: "Business Rule Discovery",
      description: "Use profiling insights to discover implicit business rules in your data that should be codified into explicit quality checks."
    }
  ];

  return (
    <FeaturePageTemplate
      title="Data Profiling"
      description="Gain immediate insights into your data's structure and quality through comprehensive profiling that analyzes distributions, patterns, and potential issues without writing code."
      icon={icon}
      benefits={benefits}
      details={details}
      useCases={useCases}
      additionalContent={
        <div className="bg-gray-50 p-8 rounded-lg">
          <h3 className="text-2xl font-bold mb-4">Profiling Engine</h3>
          <p className="mb-4">
            Qupid's profiling engine is built for scale, using distributed computing techniques to profile even the largest datasets efficiently. Profiling jobs automatically sample data when appropriate to deliver insights quickly without unnecessary processing.
          </p>
          <p>
            Profiles can be scheduled or run on-demand, with results stored historically to track changes in your data's characteristics over time. All profiling results are exportable in various formats for sharing with stakeholders or integration with other tools.
          </p>
        </div>
      }
    />
  );
};

export default ProfilingPage; 